import React from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
} from 'components/icons';

/**
 * Renders the analyst-agent's structured verdict.
 *
 * The shape is produced by the agent and is not typed end to end — the
 * backend's own fallback object doesn't even match what comes back — so every
 * field is treated as optional and the caller keeps a plain-text path for
 * anything this doesn't recognise.
 */
export interface AnalysisPayload {
  win_probability?: number;
  recommendation?: string;
  confidence?: string;
  rationale?: string;
  strengths?: string[];
  gaps?: string[];
  risks?: string[];
  key_terms?: Record<string, string>;
  next_steps?: string[];
}

/** True when there is enough structure here to be worth rendering as a report. */
export function isAnalysisPayload(value: any): value is AnalysisPayload {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return (
    typeof value.win_probability === 'number' ||
    Array.isArray(value.strengths) ||
    Array.isArray(value.risks) ||
    Array.isArray(value.next_steps)
  );
}

/** Accepts 0–1 or 0–100 — the agent is not consistent about which it sends. */
function toPercent(value: number): number {
  const pct = value <= 1 ? value * 100 : value;
  return Math.max(0, Math.min(100, Math.round(pct)));
}

const RECOMMENDATION_STYLES: Record<string, string> = {
  pursue: 'bg-green-100 text-green-800',
  bid: 'bg-green-100 text-green-800',
  'no-bid': 'bg-red-100 text-red-800',
  no_bid: 'bg-red-100 text-red-800',
  pass: 'bg-red-100 text-red-800',
  monitor: 'bg-amber-100 text-amber-800',
};

const humanise = (s: string) => s.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const List: React.FC<{
  title: string;
  items: string[];
  icon: React.ComponentType<{ className?: string }>;
  tone: string;
}> = ({ title, items, icon: Icon, tone }) => (
  <div>
    <h4 className="flex items-center gap-1.5 text-sm font-semibold text-content mb-2">
      <Icon className={`h-4 w-4 ${tone}`} aria-hidden="true" />
      {title}
      <span className="text-xs font-normal text-content-muted">({items.length})</span>
    </h4>
    <ul className="space-y-1.5">
      {items.map((item, i) => (
        <li key={i} className="flex gap-2 text-sm text-content-muted">
          <span className={`mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0 ${tone.replace('text-', 'bg-')}`} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

export interface TermEvidenceView { evidence: string; source?: string; found?: string }

const EVIDENCE_LABEL: Record<string, { text: string; cls: string }> = {
  documented: { text: 'read in', cls: 'bg-green-100 text-green-800' },
  partly_documented: { text: 'partly read in', cls: 'bg-green-50 text-green-800' },
  from_record: { text: 'matches the SAM.gov record', cls: 'bg-blue-100 text-blue-800' },
  not_found: { text: 'inferred; not found in the documents', cls: 'bg-amber-100 text-amber-900' },
  not_checkable: { text: 'not stated by the analysis', cls: 'bg-surface-sunk text-content-muted' },
  no_readable_documents: { text: 'no readable documents to check', cls: 'bg-surface-sunk text-content-muted' },
};

const AnalysisResult: React.FC<{ data: AnalysisPayload; evidence?: Record<string, TermEvidenceView> | null }> = ({ data, evidence }) => {
  const pct = typeof data.win_probability === 'number' ? toPercent(data.win_probability) : null;
  const rec = (data.recommendation || '').toLowerCase();
  const barTone = pct === null ? '' : pct >= 60 ? 'bg-green-500' : pct >= 35 ? 'bg-amber-500' : 'bg-red-400';

  return (
    <div className="space-y-5">
      {(pct !== null || data.recommendation || data.confidence) && (
        <div className="flex flex-wrap items-center gap-3">
          {data.recommendation && (
            <span
              className={`text-xs font-semibold px-2.5 py-1 rounded-full uppercase tracking-wide ${
                RECOMMENDATION_STYLES[rec] || 'bg-surface-sunk text-content'
              }`}
            >
              {humanise(data.recommendation)}
            </span>
          )}
          {data.confidence && (
            <span className="text-xs text-content-muted">{humanise(data.confidence)} confidence</span>
          )}
          {pct !== null && (
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-2xl font-bold text-content tabular-nums">{pct}%</span>
              <div className="w-24">
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className={`h-1.5 rounded-full ${barTone}`} style={{ width: `${pct}%` }} />
                </div>
                <p className="text-[10px] text-content-muted mt-0.5">win probability</p>
              </div>
            </div>
          )}
        </div>
      )}

      {data.rationale && <p className="text-sm text-content-muted leading-relaxed">{data.rationale}</p>}

      {!!data.strengths?.length && (
        <List title="Strengths" items={data.strengths} icon={CheckCircleIcon} tone="text-green-600" />
      )}
      {!!data.gaps?.length && (
        <List title="Gaps to close" items={data.gaps} icon={ExclamationTriangleIcon} tone="text-amber-600" />
      )}
      {!!data.risks?.length && (
        <List title="Risks" items={data.risks} icon={ShieldExclamationIcon} tone="text-red-500" />
      )}

      {data.key_terms && Object.keys(data.key_terms).length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-content mb-2">Key terms</h4>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 rounded-lg bg-surface-sunk p-3">
            {Object.entries(data.key_terms).map(([k, v]) => (
              <div key={k}>
                <dt className="text-[10px] uppercase tracking-wide text-content-muted font-semibold">
                  {humanise(k)}
                </dt>
                <dd className="text-sm text-content break-words">{String(v)}</dd>
                {evidence?.[k] && (() => {
                  const e = EVIDENCE_LABEL[evidence[k].evidence] || { text: evidence[k].evidence, cls: 'bg-surface-sunk text-content-muted' };
                  const src = (evidence[k].evidence === 'documented' || evidence[k].evidence === 'partly_documented') && evidence[k].source ? ` ${evidence[k].source}` : '';
                  return <dd className={`mt-0.5 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${e.cls}`}>{e.text}{src}</dd>;
                })()}
              </div>
            ))}
          </dl>
        </div>
      )}

      {!!data.next_steps?.length && (
        <div>
          <h4 className="text-sm font-semibold text-content mb-2">Next steps</h4>
          <ol className="space-y-2">
            {data.next_steps.map((step, i) => (
              <li key={i} className="flex gap-2.5 text-sm text-content-muted">
                <span className="flex-shrink-0 flex items-center justify-center h-5 w-5 rounded-full bg-brand-50 text-brand-700 text-[10px] font-semibold mt-0.5">
                  {i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

export default AnalysisResult;
